(function () {
  'use strict';

  /* ========================================================================
     Moonou Images with Text — JS
     1) BG text auto-sizing (stretch to full container width)
     2) Stats count-up animation with IntersectionObserver
     ======================================================================== */

  /* ---- Easing functions ---- */

  var easings = {
    'linear': function (t) { return t; },
    'ease-out': function (t) { return 1 - (1 - t) * (1 - t); },
    'ease-in-out': function (t) {
      return t < 0.5
        ? 2 * t * t
        : 1 - (-2 * t + 2) * (-2 * t + 2) / 2;
    }
  };

  /* ---- BG Text Auto-Sizing ---- */

  function fitBgText(section) {
    var bgEl = section.querySelector('.moonou-iwt__bg');
    var textEl = section.querySelector('.moonou-iwt__bg-text');
    if (!bgEl || !textEl) return;

    var containerWidth = bgEl.offsetWidth;
    if (containerWidth <= 0) return;

    var styles = getComputedStyle(section);
    var isMobile = window.innerWidth < 768;

    var maxFs, minFs;
    if (isMobile) {
      maxFs = parseFloat(styles.getPropertyValue('--miwt-bg-max-fs-m')) || 300;
      minFs = parseFloat(styles.getPropertyValue('--miwt-bg-min-fs-m')) || 50;
    } else {
      maxFs = parseFloat(styles.getPropertyValue('--miwt-bg-max-fs')) || 300;
      minFs = 0;
    }

    // Measure at max font-size
    textEl.style.fontSize = maxFs + 'px';
    var textWidth = textEl.scrollWidth;

    if (textWidth <= 0) return;

    // Calculate optimal size to fill container, capped at max
    var optimalFs = Math.min(maxFs, (containerWidth / textWidth) * maxFs);

    // Apply minimum (mobile: text overflows instead of shrinking)
    optimalFs = Math.max(optimalFs, minFs);

    textEl.style.fontSize = Math.floor(optimalFs) + 'px';
  }

  /* ---- Count-Up Animation ---- */

  function parseStatNumber(text) {
    text = text.trim();
    var match = text.match(/^([^\d]*)([\d.,]+)(.*)$/);
    if (!match) return null;

    var prefix = match[1];
    var numStr = match[2];
    var suffix = match[3];

    // Detect thousands separator (dot in German format like "7.200")
    var useThousandsSep = numStr.indexOf('.') !== -1 && numStr.indexOf(',') === -1;

    // Parse: strip dots (thousands sep), replace comma with dot (decimal)
    var cleaned = numStr.replace(/\./g, '').replace(',', '.');
    var target = parseFloat(cleaned);

    if (isNaN(target)) return null;

    var isInteger = target === Math.floor(target);

    return {
      prefix: prefix,
      target: target,
      suffix: suffix,
      useThousandsSep: useThousandsSep,
      isInteger: isInteger
    };
  }

  function formatNumber(value, useThousandsSep, isInteger) {
    var num = isInteger ? Math.round(value) : Math.round(value * 10) / 10;
    var str = String(num);

    if (useThousandsSep && num >= 1000) {
      // German thousands separator
      var parts = str.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      str = parts.join(',');
    }

    return str;
  }

  function animateNumber(el, parsed, duration, easingName) {
    var easing = easings[easingName] || easings['ease-out'];
    var start = null;

    function step(timestamp) {
      if (!start) start = timestamp;
      var elapsed = timestamp - start;
      var progress = Math.min(elapsed / duration, 1);
      var easedProgress = easing(progress);

      var current = easedProgress * parsed.target;
      el.textContent = parsed.prefix + formatNumber(current, parsed.useThousandsSep, parsed.isInteger) + parsed.suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    // Start at 0
    el.textContent = parsed.prefix + formatNumber(0, parsed.useThousandsSep, parsed.isInteger) + parsed.suffix;
    requestAnimationFrame(step);
  }

  function initCountUp(container) {
    var numberEls = container.querySelectorAll('.moonou-iwt__stat-number');
    var duration = parseInt(container.getAttribute('data-countup-duration'), 10) || 2000;
    var stagger = parseInt(container.getAttribute('data-countup-stagger'), 10) || 200;
    var easingName = container.getAttribute('data-countup-easing') || 'ease-out';

    // Parse all numbers and store originals
    var items = [];
    numberEls.forEach(function (el) {
      var originalText = el.getAttribute('data-original') || el.textContent;
      // Store original text for re-animation (theme editor reload)
      if (!el.getAttribute('data-original')) {
        el.setAttribute('data-original', el.textContent);
      }
      var parsed = parseStatNumber(originalText);
      if (parsed) {
        items.push({ el: el, parsed: parsed });
        // Set to 0 immediately
        el.textContent = parsed.prefix + formatNumber(0, parsed.useThousandsSep, parsed.isInteger) + parsed.suffix;
      }
    });

    // Animate with stagger
    items.forEach(function (item, index) {
      setTimeout(function () {
        animateNumber(item.el, item.parsed, duration, easingName);
      }, index * stagger);
    });
  }

  /* ---- Initialization ---- */

  function initSection(section) {
    // BG text auto-sizing
    fitBgText(section);

    // ResizeObserver for responsive BG text
    var bgEl = section.querySelector('.moonou-iwt__bg');
    if (bgEl && typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        fitBgText(section);
      });
      ro.observe(bgEl);
      section._moonouIwtRo = ro;
    }

    // Count-up: IntersectionObserver
    var statsEl = section.querySelector('[data-countup]');
    if (statsEl && typeof IntersectionObserver !== 'undefined') {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            initCountUp(statsEl);
            observer.unobserve(statsEl);
          }
        });
      }, { threshold: 0.3 });
      observer.observe(statsEl);
      section._moonouIwtObs = observer;
    }
  }

  function cleanupSection(section) {
    if (section._moonouIwtRo) {
      section._moonouIwtRo.disconnect();
      section._moonouIwtRo = null;
    }
    if (section._moonouIwtObs) {
      section._moonouIwtObs.disconnect();
      section._moonouIwtObs = null;
    }
  }

  function initAll() {
    var sections = document.querySelectorAll('.moonou-iwt');
    sections.forEach(function (section) {
      cleanupSection(section);
      initSection(section);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('.moonou-iwt');
    if (section) {
      cleanupSection(section);
      initSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector('.moonou-iwt');
    if (section) {
      cleanupSection(section);
    }
  });
})();
